import { lazy, Suspense } from "react";
import { Navigate, Route, createRoutesFromElements } from "react-router-dom";

import RouteLoadingFallback from "@components/RouteLoadingFallback";
import AppLayout from "../features/AppLayout";

import { applicationSpaRoutes } from "@shared/constants/routes";

import { preloadRoute } from "@utils";

import { FallBack, ProtectedRoute, LogoutOnlyRoute, MagicLinkOrProtectedRoute } from "./RouteGuards";
import { TwoFuturePowBundleAppRoutes } from "./SimpleDocumentsCreation/TwoFuturePowBundle/routes";

const loadProfileEdit = () => import("./Profile/routes/Edit");
const ProfileEdit = lazy(loadProfileEdit);
const loadProfileChangePassword = () => import("./Profile/routes/ChangePassword");
const ProfileChangePassword = lazy(loadProfileChangePassword);
const loadProfileBookings = () => import("./Profile/routes/Bookings");
const ProfileBookings = lazy(loadProfileBookings);
const loadPageNotFound = () => import("./PageNotFound");
const PageNotFound = lazy(loadPageNotFound);
const loadConsentBorgerDK = () => import("./ConsentBorgerDK");
const ConsentBorgerDK = lazy(loadConsentBorgerDK);
const loadCreateDocument = () => import("./CreateDocument");
const CreateDocument = lazy(loadCreateDocument);
const loadLogin = () => import("./Login");
const Login = lazy(loadLogin);
const loadNotifications = () => import("./Notifications");
const Notifications = lazy(loadNotifications);
const loadMyOverview = () => import("./MyOverview");
const MyOverview = lazy(loadMyOverview);
const loadMyBooking = () => import("./Booking");
const MyBooking = lazy(loadMyBooking);
const loadMyDocuments = () => import("./MyDocuments");
const MyDocuments = lazy(loadMyDocuments);
const loadIdentityDocuments = () => import("./IdentityDocuments");
const IdentityDocuments = lazy(loadIdentityDocuments);
const loadUploadId = () => import("./UploadId");
const UploadId = lazy(loadUploadId);
const loadApproveDocument = () => import("./ApproveDocument");
const ApproveDocument = lazy(loadApproveDocument);
const loadLawdocsFlow = () => import("./LawdocsFlow");
const LawdocsFlow = lazy(loadLawdocsFlow);
const loadGetReadyForMeeting = () => import("./GetReadyForMeeting");
const GetReadyForMeeting = lazy(loadGetReadyForMeeting);
const loadGetReadyForMeetingRequestLink = () => import("./GetReadyForMeeting/routes/RequestLink");
const GetReadyForMeetingRequestLink = lazy(loadGetReadyForMeetingRequestLink);
const loadDigitalOnboarding = () => import("./DigitalOnboarding");
const DigitalOnboarding = lazy(loadDigitalOnboarding);
const loadMeetingPreparation = () => import("./MeetingPreparation");
const MeetingPreparation = lazy(loadMeetingPreparation);
const loadMyDocumentsPreview = () => import("./MyDocumentsPreview");
const MyDocumentsPreview = lazy(loadMyDocumentsPreview);
const loadMyDocumentsPreviewRequestLink = () => import("./MyDocumentsPreview/routes/RequestLink");
const MyDocumentsPreviewRequestLink = lazy(loadMyDocumentsPreviewRequestLink);
const loadCompleteRegistration = () => import("./CompleteRegistration");
const CompleteRegistration = lazy(loadCompleteRegistration);
const loadRequestLink = () => import("./RequestLink");
const RequestLink = lazy(loadRequestLink);
const loadCampaignVoucher = () => import("./CampaignVoucher");
const CampaignVoucher = lazy(loadCampaignVoucher);

const loadSimpleDocumentsCreationFuturePow = () => import("./SimpleDocumentsCreation/FuturePow");
const SimpleDocumentsCreationFuturePow = lazy(loadSimpleDocumentsCreationFuturePow);
const loadSimpleDocumentsCreationFuturePowGeneralInformation = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/GeneralInformation");
const SimpleDocumentsCreationFuturePowGeneralInformation = lazy(loadSimpleDocumentsCreationFuturePowGeneralInformation);
const loadSimpleDocumentsCreationFuturePowCustomizationSettings = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/CustomizationSettings");
const SimpleDocumentsCreationFuturePowCustomizationSettings = lazy(
  loadSimpleDocumentsCreationFuturePowCustomizationSettings,
);
const loadSimpleDocumentsCreationFuturePowRemuneration = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/Remuneration");
const SimpleDocumentsCreationFuturePowRemuneration = lazy(loadSimpleDocumentsCreationFuturePowRemuneration);
const loadSimpleDocumentsCreationFuturePowGreaterGifts = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/GreaterGifts");
const SimpleDocumentsCreationFuturePowGreaterGifts = lazy(loadSimpleDocumentsCreationFuturePowGreaterGifts);
const loadSimpleDocumentsCreationFuturePowGrantorInformation = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/GrantorInformation");
const SimpleDocumentsCreationFuturePowGrantorInformation = lazy(loadSimpleDocumentsCreationFuturePowGrantorInformation);
const loadSimpleDocumentsCreationFuturePowAgentsInformation = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/AgentsInformation");
const SimpleDocumentsCreationFuturePowAgentsInformation = lazy(loadSimpleDocumentsCreationFuturePowAgentsInformation);
const loadSimpleDocumentsCreationFuturePowAgentsAgreement = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/AgentsAgreement");
const SimpleDocumentsCreationFuturePowAgentsAgreement = lazy(loadSimpleDocumentsCreationFuturePowAgentsAgreement);
const loadSimpleDocumentsCreationFuturePowDocumentCreationLoading = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/DocumentCreationLoading");
const SimpleDocumentsCreationFuturePowDocumentCreationLoading = lazy(
  loadSimpleDocumentsCreationFuturePowDocumentCreationLoading,
);
const loadSimpleDocumentsCreationFuturePowApproveDocument = () =>
  import("./SimpleDocumentsCreation/FuturePow/steps/ApproveDocument");
const SimpleDocumentsCreationFuturePowApproveDocument = lazy(loadSimpleDocumentsCreationFuturePowApproveDocument);

export default createRoutesFromElements(
  <Route path="/" element={<AppLayout />} errorElement={<FallBack />} hydrateFallbackElement={<RouteLoadingFallback />}>
    <Route
      lazy={preloadRoute(loadPageNotFound)}
      path="/"
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <PageNotFound />
          </Suspense>
        </ProtectedRoute>
      }
    />
    {/* Protected routes */}
    <Route
      lazy={preloadRoute(loadProfileEdit)}
      path={applicationSpaRoutes.profileEdit}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <ProfileEdit />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadProfileBookings)}
      path={applicationSpaRoutes.profileBookings}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <ProfileBookings />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadProfileChangePassword)}
      path={applicationSpaRoutes.profileChangePassword}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <ProfileChangePassword />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadCreateDocument)}
      path={applicationSpaRoutes.createDocument}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <CreateDocument />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadNotifications)}
      path={applicationSpaRoutes.notifications}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Notifications />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadMyOverview)}
      path={applicationSpaRoutes.myOverview}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <MyOverview />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadMyBooking)}
      path={`${applicationSpaRoutes.myBookings}/:id`}
      element={
        <MagicLinkOrProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <MyBooking />
          </Suspense>
        </MagicLinkOrProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadMyDocuments)}
      path={applicationSpaRoutes.myDocuments}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <MyDocuments />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadIdentityDocuments)}
      path={applicationSpaRoutes.identityDocuments}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <IdentityDocuments />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      lazy={preloadRoute(loadApproveDocument)}
      path={`${applicationSpaRoutes.approveDocument}/:id`}
      element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <ApproveDocument />
          </Suspense>
        </ProtectedRoute>
      }
    />
    <Route
      path={applicationSpaRoutes.products}
      element={<Navigate to={applicationSpaRoutes.createDocument} replace={true} />}
    />
    <Route
      lazy={preloadRoute(loadLawdocsFlow)}
      path={applicationSpaRoutes.lawdocs}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <LawdocsFlow />
        </Suspense>
      }
    />
    {/* Logout only routes */}
    <Route
      lazy={preloadRoute(loadLogin)}
      path={applicationSpaRoutes.login}
      element={
        <LogoutOnlyRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Login />
          </Suspense>
        </LogoutOnlyRoute>
      }
    />
    {/* Other routes */}
    <Route
      lazy={preloadRoute(loadDigitalOnboarding)}
      path={applicationSpaRoutes.digitalOnboarding}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <DigitalOnboarding />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadMeetingPreparation)}
      path={applicationSpaRoutes.meetingPreparation}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <MeetingPreparation />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadMyDocumentsPreview)}
      path={applicationSpaRoutes.myDocumentsPreview}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <MyDocumentsPreview />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadMyDocumentsPreviewRequestLink)}
      path={applicationSpaRoutes.myDocumentsPreviewRequestLink}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <MyDocumentsPreviewRequestLink />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadCompleteRegistration)}
      path={applicationSpaRoutes.completeRegistration}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <CompleteRegistration />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadRequestLink)}
      path={applicationSpaRoutes.requestLink}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <RequestLink />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadCampaignVoucher)}
      path={`${applicationSpaRoutes.campaign}/:voucherCode`}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <CampaignVoucher />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadConsentBorgerDK)}
      path={applicationSpaRoutes.consentBorgerDK}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <ConsentBorgerDK />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadUploadId)}
      path={applicationSpaRoutes.uploadId}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <UploadId />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadGetReadyForMeeting)}
      path={applicationSpaRoutes.getReadyForMeeting}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <GetReadyForMeeting />
        </Suspense>
      }
    />
    <Route
      lazy={preloadRoute(loadGetReadyForMeetingRequestLink)}
      path={applicationSpaRoutes.getReadyForMeetingRequestLink}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <GetReadyForMeetingRequestLink />
        </Suspense>
      }
    />
    {TwoFuturePowBundleAppRoutes}
    <Route
      lazy={preloadRoute(loadSimpleDocumentsCreationFuturePow)}
      path={applicationSpaRoutes.simpleDocumentCreation.futurePow.generalInformation}
      element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <SimpleDocumentsCreationFuturePow />
        </Suspense>
      }
    >
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowGeneralInformation)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.generalInformation}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowGeneralInformation />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowCustomizationSettings)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.customizationSettings}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowCustomizationSettings />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowRemuneration)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.remuneration}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowRemuneration />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowGreaterGifts)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.greaterGifts}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowGreaterGifts />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowGrantorInformation)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.grantorInformation}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowGrantorInformation />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowAgentsInformation)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.agentsInformation}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowAgentsInformation />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowAgentsAgreement)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.agentsAgreement}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowAgentsAgreement />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowDocumentCreationLoading)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.documentCreationLoading}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowDocumentCreationLoading />
          </Suspense>
        }
      />
      <Route
        lazy={preloadRoute(loadSimpleDocumentsCreationFuturePowApproveDocument)}
        path={applicationSpaRoutes.simpleDocumentCreation.futurePow.approveDocument}
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SimpleDocumentsCreationFuturePowApproveDocument />
          </Suspense>
        }
      />
    </Route>
    <Route
      lazy={preloadRoute(loadPageNotFound)}
      path="*"
      element={
        <Suspense fallback="">
          <PageNotFound />
        </Suspense>
      }
    />
  </Route>,
);
